import { useCallback, useState } from "react";

export function useLocalStorage<T>(key: string, defaultValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return defaultValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue],
  );

  const removeValue = useCallback(() => {
    try {
      setStoredValue(defaultValue);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, defaultValue]);

  return [storedValue, setValue, removeValue] as const;
}

interface TableFilters {
  globalFilter: string;
  columnFilters: Record<string, unknown>;
  archive: boolean;
  activeFilter: string;
  birthDateRange: { start: string; end: string };
  contractDateRange: { start: string; end: string };
}

const DEFAULT_TABLE_FILTERS: TableFilters = {
  globalFilter: "",
  columnFilters: {},
  archive: false,
  activeFilter: "",
  birthDateRange: { start: "", end: "" },
  contractDateRange: { start: "", end: "" },
};

export function useTableFilters(tableId: string) {
  const storageKey = `table-filters-${tableId}`;

  const [filters, setFilters, clearFilters] = useLocalStorage<TableFilters>(
    storageKey,
    DEFAULT_TABLE_FILTERS,
  );

  const updateFilter = useCallback(
    (key: keyof TableFilters, value: unknown) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setFilters],
  );

  const updateDateRange = useCallback(
    (rangeType: "birthDateRange" | "contractDateRange", field: "start" | "end", value: string) => {
      setFilters((prev) => ({
        ...prev,
        [rangeType]: {
          ...prev[rangeType],
          [field]: value,
        },
      }));
    },
    [setFilters],
  );

  const hasActiveFilters = useCallback(() => {
    return !!(
      filters.globalFilter ||
      Object.keys(filters.columnFilters).length > 0 ||
      filters.archive ||
      filters.activeFilter ||
      filters.birthDateRange.start ||
      filters.birthDateRange.end ||
      filters.contractDateRange.start ||
      filters.contractDateRange.end
    );
  }, [filters]);

  return {
    filters,
    setFilters,
    clearFilters,
    updateFilter,
    updateDateRange,
    hasActiveFilters,
  };
}
